import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PassportProvenanceBadge } from '@/components/passport/PassportProvenanceBadge';
import { ISSUER_REMOVED_LABEL } from '@/components/passport/EducationAttestationBadge';

interface Props {
  studentUserId: string;
  shopId?: string | null;
}

const TYPE_LABELS: Record<string, string> = {
  enrollment: 'Enrollment',
  attendance: 'Attendance',
  skills: 'Skills',
  completion: 'Completion',
  recommendation: 'Recommendation',
};

interface SummaryRow {
  verification_id: string;
  verification_type: string;
  school_name: string | null;
  status: string;
  verified_at: string;
  notes: string | null;
  attested_by_name: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  issuer_removed: boolean | null;
}

/**
 * Core-100 R4w2 — CORE-T provenance: each attestation names the staff member who made it, and a withdrawn
 * attestation stays in the student's own view as withdrawn, with when and why (it is never shown as standing, and
 * the shop-side summary never returns it). A missing staff name reads "a staff member", never a guess.
 */
export function attestedByLabel(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  return `Attested by ${n || 'a staff member'}`;
}

export function StudentVerificationSummary({ studentUserId, shopId }: Props) {
  const { data: verifications, isLoading } = useQuery({
    queryKey: ['student-verification-summary', studentUserId, shopId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_student_verification_summary', {
        p_student_user_id: studentUserId,
        p_shop_id: shopId || null,
      });
      if (error) throw error;
      return ((data ?? []) as unknown) as SummaryRow[];
    },
    enabled: !!studentUserId,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!verifications?.length) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          {/* OD-35: a school's attestation is a confirmation by the issuer, not a verification with evidence. */}
          <CardTitle className="text-lg">School Confirmations</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {verifications.map((v) => (
            <div
              key={v.verification_id}
              data-testid={`student-verification-${v.status}`}
              className={`flex items-center justify-between rounded-lg border p-3${v.status === 'revoked' ? ' opacity-70' : ''}`}
            >
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {TYPE_LABELS[v.verification_type] || v.verification_type}
                  </Badge>
                  <span className="text-sm font-medium">{v.school_name}</span>
                  {/* CORE-C GAP-002. Per ROW, because each verification is a
                      separate school standing behind a separate fact.
                      `status` is the OWNER's field: the RPC already returns
                      only 'verified' rows, and this reads that rather than
                      re-deciding the lifecycle from CORE-C. OD-35: the badge
                      reads "Confirmed by <school>". A row whose name
                      did not come back falls to the generic wording instead
                      of having an id presented as an institution. */}
                  {v.status === 'verified' && (
                    <PassportProvenanceBadge
                      factGroupId="student-verification"
                      attributedTo={v.school_name ?? undefined}
                    />
                  )}
                  {v.status === 'revoked' && (
                    <Badge variant="secondary" className="text-xs">Withdrawn</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{attestedByLabel(v.attested_by_name)}</p>
                {/* Core-100 R5 (CORE-M Prov / CORE-T D): the server returns issuer_removed to the student AND to a
                    hiring shop; it was dropped here, so a deleted school's attestation read exactly like a standing
                    school's. The attestation stays (OD-02/03) and states the fact beside it. */}
                {v.issuer_removed === true && (
                  <p className="text-xs text-muted-foreground" data-testid="student-verification-issuer-removed">
                    {ISSUER_REMOVED_LABEL}
                  </p>
                )}
                {v.status === 'revoked' && (
                  <p className="text-xs text-muted-foreground">
                    Withdrawn{v.revoked_at ? ` ${new Date(v.revoked_at).toLocaleDateString()}` : ''}
                    {v.revoke_reason ? `: ${v.revoke_reason}` : ''}
                  </p>
                )}
                {v.notes && (
                  <p className="text-xs text-muted-foreground">{v.notes}</p>
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                {new Date(v.verified_at).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
